import React from 'react';
import { PoolResponse } from '../../api/generated/models';
import DropDown from './DropDown';
import './ManualPoolAssignment.scss';

export type PoolSplit = {
    poolId: string;
    splitPercent: string;
};

interface ManualPoolAssignmentProps {
    pools: PoolResponse[];
    isLoadingPools: boolean;
    poolSplits: PoolSplit[];
    onPoolSplitsChange: (poolSplits: PoolSplit[]) => void;
}

const getPoolOptions = (pools: PoolResponse[]) =>
    pools
        .filter((pool) => pool.poolID != null)
        .map((pool) => ({
            label: pool.poolLongName
                ? `${pool.poolShortName} - ${pool.poolLongName}`
                : (pool.poolShortName ?? `Pool ${pool.poolID}`),
            value: String(pool.poolID),
        }));

export const isValidPoolAssignment = (poolSplits: PoolSplit[]) => {
    const selectedPoolIds = poolSplits.map((split) => Number(split.poolId));
    const splitTotal = poolSplits.reduce(
        (total, split) => total + (Number(split.splitPercent) || 0),
        0
    );
    const hasPositivePercentages = poolSplits.every(
        (split) => Number(split.splitPercent) > 0
    );
    const hasUniquePools =
        selectedPoolIds.every(
            (poolId) => Number.isInteger(poolId) && poolId > 0
        ) && new Set(selectedPoolIds).size === selectedPoolIds.length;

    return (
        poolSplits.length > 0 &&
        hasPositivePercentages &&
        hasUniquePools &&
        splitTotal === 100
    );
};

const ManualPoolAssignment: React.FC<ManualPoolAssignmentProps> = ({
    pools,
    isLoadingPools,
    poolSplits,
    onPoolSplitsChange,
}) => {
    const poolOptions = getPoolOptions(pools);
    const selectedPoolIds = new Set(
        poolSplits.map((split) => split.poolId).filter(Boolean)
    );
    const canAddPool = poolSplits.length < poolOptions.length;
    const splitTotal = poolSplits.reduce(
        (total, split) => total + (Number(split.splitPercent) || 0),
        0
    );

    const updatePoolSplit = (
        index: number,
        property: keyof PoolSplit,
        value: string
    ) => {
        onPoolSplitsChange(
            poolSplits.map((split, splitIndex) =>
                splitIndex === index ? { ...split, [property]: value } : split
            )
        );
    };

    return (
        <div className="manual-pool-assignment">
            <div className="manual-pool-assignment__split-header">
                <div>
                    <strong>Split pool assignment</strong>
                    <span>Allocate across multiple pools by percentage.</span>
                </div>
                <span
                    className={`manual-pool-assignment__split-total--${
                        splitTotal === 100 ? 'valid' : 'invalid'
                    }`}
                >
                    {splitTotal.toFixed(2)}% / 100%
                </span>
            </div>

            <div className="manual-pool-assignment__split-labels">
                <span>Pool</span>
                <span>Percentage</span>
                <span />
            </div>

            <div className="manual-pool-assignment__split-list">
                {poolSplits.map((split, index) => (
                    <div
                        className="manual-pool-assignment__split-row"
                        key={`${split.poolId}-${index}`}
                    >
                        <DropDown
                            dropdownClassName="manual-pool-assignment__pool-dropdown"
                            dropdownRenderMode="portal"
                            getPopupContainer={() =>
                                document
                                    .querySelector('.manual-pool-assignment')
                                    ?.closest('[role="dialog"]') ??
                                document.body
                            }
                            data={poolOptions.filter(
                                (pool) =>
                                    pool.value === split.poolId ||
                                    !selectedPoolIds.has(pool.value)
                            )}
                            placeholder={
                                isLoadingPools ? 'Loading pools' : 'Choose pool'
                            }
                            value={split.poolId}
                            onSelect={(value) =>
                                updatePoolSplit(
                                    index,
                                    'poolId',
                                    value as string
                                )
                            }
                        />
                        <div className="manual-pool-assignment__percentage-input">
                            <input
                                aria-label={`Pool ${index + 1} percentage`}
                                type="number"
                                min="0.01"
                                max="100"
                                step="0.01"
                                value={split.splitPercent}
                                onChange={(event) =>
                                    updatePoolSplit(
                                        index,
                                        'splitPercent',
                                        event.target.value
                                    )
                                }
                            />
                            <span>%</span>
                        </div>
                        <button
                            type="button"
                            className="manual-pool-assignment__remove-pool"
                            aria-label={`Remove pool ${index + 1}`}
                            disabled={poolSplits.length === 1}
                            onClick={() =>
                                onPoolSplitsChange(
                                    poolSplits.filter(
                                        (_, splitIndex) => splitIndex !== index
                                    )
                                )
                            }
                        >
                            x
                        </button>
                    </div>
                ))}
            </div>

            <button
                type="button"
                className="manual-pool-assignment__add-pool"
                disabled={!canAddPool}
                onClick={() =>
                    onPoolSplitsChange([
                        ...poolSplits,
                        { poolId: '', splitPercent: '0' },
                    ])
                }
            >
                + Add pool
            </button>
        </div>
    );
};

export default ManualPoolAssignment;
